import { useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import PayrollDataTable from '@/modules/payroll/components/shared/PayrollDataTable';
import type { PayrollTableData } from '@/modules/payroll/utils/payRunCycle.utils';

type PayrollTableModalProps = {
  table: PayrollTableData | null;
  onClose: () => void;
};

/** Full-width table dialog for cycle breakdowns, reports and payloads. */
const PayrollTableModal = ({ table, onClose }: PayrollTableModalProps) => {
  useEffect(() => {
    if (!table) {
      return;
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [table, onClose]);

  if (!table) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 px-4 py-8"
      onClick={onClose}
    >
      <div
        className="flex max-h-full w-full max-w-6xl flex-col gap-4 rounded-lg bg-white p-6 shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="payroll-table-modal-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3
              id="payroll-table-modal-title"
              className="text-lg font-semibold text-gray-900"
            >
              {table.title}
            </h3>
            <p className="text-sm text-gray-500">{table.subtitle}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            aria-label="Close"
          >
            <FontAwesomeIcon icon={faXmark} className="h-5 w-5" />
          </button>
        </div>
        <PayrollDataTable
          columns={table.columns}
          rows={table.rows}
          maxHeightClassName="max-h-[65vh]"
        />
      </div>
    </div>
  );
};

export default PayrollTableModal;
